// Anonymous public forum landing — indexable list of all questions.
//
// Lives at /forum (NO /app prefix → no auth gate). Crawlers (Google,
// Bing) hit this page and follow the inline links to /forum/{slug}
// detail pages. The page deliberately mirrors the in-app forum visually
// so signed-up users see continuity when they later navigate inside.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, ArrowRight, ChevronUp } from "lucide-react";
import { metaFor, timeAgo } from "@/lib/forumCategories";

const backend = process.env.REACT_APP_BACKEND_URL;

export default function PublicForumIndex() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set page title for crawlers.
    document.title = "Real Estate Wholesaling Forum — iSpeedToClose";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute(
      "content",
      "Q&A from real estate wholesalers — contracts, skip tracing, closing, marketing. Free to read. Sign up to post.",
    );
    fetch(`${backend}/api/public/forum/posts?limit=50`)
      .then((r) => r.json())
      .then((data) => { setPosts(data.items || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="public-forum-index">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-emerald-500 flex items-center justify-center text-white font-bold">i</div>
            <span className="font-bold text-zinc-900">iSpeedToClose</span>
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold text-sm px-4 py-2 rounded-full"
          >
            Start free <ArrowRight size={13} />
          </Link>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-600 mb-3">
            <MessageCircle size={14} /> Community Forum
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-zinc-900 leading-tight mb-3">
            Wholesaling Q&A from the trenches.
          </h1>
          <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
            Free to read. No paywall, no signup. Sign up to post or reply.
          </p>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-white rounded-lg border animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-3" data-testid="public-forum-list">
            {posts.map((p) => {
              const meta = metaFor(p.category);
              const Icon = meta.icon;
              return (
                <Link
                  key={p.id}
                  to={`/forum/${p.slug}`}
                  className="block bg-white border rounded-lg p-5 hover:border-zinc-400 transition"
                  data-testid={`public-forum-card-${p.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex flex-col items-center w-12 flex-shrink-0 text-zinc-500">
                      <ChevronUp size={14} />
                      <div className="text-sm font-bold text-zinc-700">{p.score_cached || 0}</div>
                      <div className="text-[10px] uppercase tracking-wide">votes</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border"
                          style={{ borderColor: meta.accent + "55", color: meta.accent, background: meta.accent + "10" }}
                        >
                          <Icon size={11} /> {(p.category || "").replace(/-/g, " ")}
                        </span>
                        <span className="text-xs text-zinc-500">{p.reply_count || 0} replies</span>
                      </div>
                      <h2 className="font-semibold text-lg text-zinc-900 leading-snug">{p.title}</h2>
                      <div className="text-xs text-zinc-500 mt-1">{timeAgo(p.created_at)}</div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <div className="mt-12 text-center bg-zinc-900 text-white rounded-2xl p-8">
          <h2 className="font-display font-bold text-2xl mb-2">Want to ask your own question?</h2>
          <p className="text-white/70 text-sm mb-4">
            Join the iSpeedToClose community — free to sign up, free to ask.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold text-sm px-6 py-3 rounded-full"
            data-testid="public-forum-signup-cta"
          >
            Start free <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
